import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OperationType, ServiceType } from '@common/enums';
import { ChannelItem, DriveItemDetails, TeamFile, TeamItem, TeamOdataPost, TeamPost } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services/shared.odata.service';
import {
  RestoreTeamsItemsPayload,
  RestoreTeamsParameters,
  RestoreTeamsParametersPayload
} from '@pages/teams/modals/restore-team-item-modal/restore-team-item-modal.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TeamsOdataService extends SharedOdataService<TeamItem> {
  private readonly http = inject(HttpClient);

  private readonly driveItemsService = this.odataFactory.CreateService<DriveItemDetails>('DriveItems');
  private readonly odataMethods = this.odataFactory.CreateService<any>('');

  constructor() {
    super('Teams');
    this.orderBy = 'Name asc';
  }

  getTeams(): Observable<ODataPagedResult<TeamItem>> {
    return this.odataFactory
      .CreateService<TeamItem>('Teams')
      .Query()
      .Top(this.pageSize)
      .Skip(this.pageSize * (this.page - 1))
      .Filter(this.filter)
      .OrderBy(this.orderBy)
      .ExecWithCount();
  }

  getChannels(teamId: string): Observable<ODataPagedResult<TeamItem>> {
    return this.odataFactory
      .CreateService<TeamItem>(`/Teams(${teamId})/Channels`)
      .Query()
      .Top(this.pageSize)
      .Skip(this.pageSize * (this.page - 1))
      .Filter(this.filter)
      .OrderBy(this.orderBy)
      .ExecWithCount();
  }

  getChannelItems(channelId: string): Observable<ChannelItem[]> {
    return this.odataFactory
      .CreateService(`/Channels(${channelId})/TeamThirdLevelItems`)
      .Query()
      .CustomQueryOptions([{ key: 'Id', value: channelId }])
      .Exec()
      .pipe(
        map((items: ChannelItem[]) =>
          items.map((item: ChannelItem) => {
            item.ChannelId = channelId;
            return item;
          })
        )
      );
  }

  getPosts(channelId: string, skip: number, top: number): Observable<ODataPagedResult<TeamPost>> {
    return this.odataFactory
      .CreateService<TeamOdataPost>(`/Channels(${channelId})/ChatMessages`)
      .Query()
      .OrderBy('CreatedDate desc')
      .Top(top)
      .Skip(skip)
      .ExecWithCount()
      .pipe(map(({ data, count }) => ({ nextLink: null, data: data.map((post) => new TeamPost(post)), count })));
  }

  getReplies(messageId: number): Observable<TeamPost[]> {
    return this.odataFactory
      .CreateService<TeamOdataPost>(`/ChatMessages(${messageId})/Replies`)
      .Query()
      .OrderBy('CreatedDate asc')
      .Exec()
      .pipe(
        map((posts: TeamOdataPost[]) => posts.map((post) => new TeamPost(post)).toSorted((a, b) => a.date.getTime() - b.date.getTime()))
      ) as Observable<TeamPost[]>;
  }

  getFiles(channelId: string, treeId: string): Observable<ODataPagedResult<TeamFile>> {
    return this.odataFactory.CreateService<TeamFile>(`Channels(${channelId})/Files?Id=${treeId}`).Query().ExecWithCount();
  }

  getFileDetails(id: string): Observable<DriveItemDetails> {
    return this.driveItemsService.CustomFunction(id, 'GetDetails', null).pipe(map((res) => res.body)) as Observable<DriveItemDetails>;
  }

  restoreTeamItems(payload: RestoreTeamsItemsPayload): Observable<void> {
    return this.odataMethods.CustomCollectionAction('RestoreTeamsItems', payload);
  }

  restoreTeamsParameters(payload: RestoreTeamsParametersPayload): Observable<RestoreTeamsParameters> {
    return this.odataMethods.CustomCollectionAction('RestoreTeamsParameters', payload).pipe(map((res) => res.body));
  }

  checkAppPermissions(serviceType: ServiceType, operationType: OperationType): Observable<boolean> {
    const odataService = this.odataFactory.CreateService(
      `/CheckAppPermissions(serviceType='${serviceType}',operationType='${operationType}')`
    );

    return this.http.get<{ Allowed: boolean }>(odataService.Query().GetUrl()).pipe(map((res) => res.Allowed));
  }
}
